import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Clipboard } from '@angular/cdk/clipboard';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TenantService } from 'app/services/tenant.service';
import * as tenantActions from 'app/actions/tenant';
import * as fromTenant from 'app/reducers/tenant';
import { TenantReducerUtils } from 'app/utils/reducers/tenant-reducer.utils';
import { RegenerateTenantBody, RegenerateTenantDto, TenantDto, TenantUpdateDto } from 'app/models/api/tenant';
import { TenantAccount } from 'app/models/reducers/tenant';
import { ThemeService } from 'app/services/theme/theme.service';
import { concatLatestFrom } from '@ngrx/operators';
import { ApplicationStore } from 'app/state-management/application/application.store';

@Injectable()
export class TenantEffects {
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);
  private tenantService: TenantService = inject(TenantService);
  private translateService: TranslateService = inject(TranslateService);
  private clipboard: Clipboard = inject(Clipboard);
  private themeService: ThemeService = inject(ThemeService);
  private appStore: ApplicationStore = inject(ApplicationStore);

  pageEnter$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.pageEnter),
    map(() => tenantActions.loadTenantInitial()),
  ));

  copySignUpKeyToClipboard$: Observable<unknown> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.copySignUpKeyToClipboard),
    concatLatestFrom(() => this.store.select(fromTenant.selectSignUpKey)),
    tap(([, signUpKey]: [Action, string]) => this.clipboard.copy(signUpKey)),
    tap(() => {
      const text: string = this.translateService.instant('TENANT.SNACK.COPIED_TEXT');
      this.appStore.showSnack({ text });
    }),
  ), { dispatch: false });

  shareLink$: Observable<unknown> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.shareSignUpKeyLink),
    concatLatestFrom(() => [this.store.select(fromTenant.selectSignUpKey), this.themeService.signupUrl$]),
    tap(([, signUpKey, signUpUrl]: [Action, string, string]) => {
      const link: string = TenantReducerUtils.createShareLink(signUpUrl, signUpKey);
      this.clipboard.copy(link);
    }),
    tap(() => {
      const text: string = this.translateService.instant('TENANT.SNACK.SHARE_LINK_TEXT');
      this.appStore.showSnack({ text });
    }),
  ), { dispatch: false });

  // API effects
  loadTenantInitial$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.loadTenantInitial),
    switchMap(() => this.tenantService.getTenant().pipe(
      map((response: TenantDto) => tenantActions.loadTenantInitialSuccess({ response })),
      catchError(error => of(tenantActions.loadTenantInitialFailed({ error }))),
    )),
  ));

  regenerateSignUpKey$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.regenerateSignUpKey),
    concatLatestFrom(() => [
      this.store.select(fromTenant.selectTenantId),
      this.store.select(fromTenant.selectSignUpKey),
    ]),
    map(([, id, oldSignUpKey]: [Action, string, string]) => ({ id, oldSignUpKey })),
    switchMap((body: RegenerateTenantBody) => this.tenantService.regenerateSignUpKey(body).pipe(
      map((response: RegenerateTenantDto) => tenantActions.regenerateSignUpKeySuccess({ response: response.signUpKey })),
      catchError(error => of(tenantActions.regenerateSignUpKeyFail({ error }))),
    )),
  ));

  saveTenant$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tenantActions.saveTenant),
    concatLatestFrom(() => [
      this.store.select(fromTenant.selectTenantId),
      this.store.select(fromTenant.selectName),
      this.store.select(fromTenant.selectAccounts),
    ]),
    map(([, tenantId, name, accounts]: [Action, string, string, TenantAccount[]]) =>
      TenantReducerUtils.createTenantUpdateDto(tenantId, name, accounts)),
    switchMap((body: TenantUpdateDto) => this.tenantService.saveTenant(body).pipe(
      map((response: TenantDto) => tenantActions.saveTenantSuccess({ response })),
      catchError(error => of(tenantActions.saveTenantFail({ error }))),
    )),
  ));
}
